// src/services/geoipService.js
const GEOIP_CACHE_KEY = "inssa:geoip:v1";
const GEOIP_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
// The GeoIP lookup gates the initial map-center cascade, which in turn gates
// the nearby-discovery (gems/offers) fetch. An unbounded fetch here — e.g. a
// cold Cloud Function — stalls that whole chain, so cap it aggressively and let
// callers fall through to the next center source.
const GEOIP_FETCH_TIMEOUT_MS = 4000;

function getGeoIpUrl() {
  const base = (process.env.REACT_APP_FIREBASE_BACKEND || "").replace(/\/+$/, "");

  if (!base) {
    throw new Error("GeoIP base URL missing. Set REACT_APP_FIREBASE_BACKEND");
  }

  return `${base}/geoip_getApproxLocation`;
}

function isCacheableResult(data) {
  // Only cache positive hits to avoid “poisoning” a device for 24 hours
  return Boolean(data && typeof data === "object" && data.found === true);
}

function readCache() {
  try {
    const raw = localStorage.getItem(GEOIP_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const ts = parsed.ts;
    const data = parsed.data;

    if (typeof ts !== "number" || !data || typeof data !== "object") return null;
    if (Date.now() - ts > GEOIP_CACHE_TTL_MS) return null;

    // Only return cache if it was a positive hit
    if (!isCacheableResult(data)) return null;

    return data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    if (!isCacheableResult(data)) {
      // Ensure we don’t keep stale/negative cached values around
      localStorage.removeItem(GEOIP_CACHE_KEY);
      return;
    }

    localStorage.setItem(GEOIP_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // ignore storage write errors
  }
}

export async function fetchApproxLocationFromIp({ force = false } = {}) {
  if (!force) {
    const cached = readCache();
    if (cached) return cached;
  }

  const url = getGeoIpUrl();

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), GEOIP_FETCH_TIMEOUT_MS)
    : null;

  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(`GeoIP request timed out after ${GEOIP_FETCH_TIMEOUT_MS}ms`);
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  if (!res.ok) {
    throw new Error(`GeoIP request failed: ${res.status}`);
  }

  const data = await res.json();

  // Cache only positive results
  writeCache(data);

  return data;
}
